import {Box, Typography, useTheme} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import {grey} from "@material-ui/core/colors";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import FileIcon from "view/components/FileIcon";
import NewTabIcon from "@material-ui/icons/OpenInNew";
import HistoryIcon from "@material-ui/icons/History";
import React, {ReactNode} from "react";
import type {UnpackedAttachmentFile as TaskAttachment} from "ts/api/attachmentFile";
import {useTranslate} from "@tolgee/react";
import DotsMenu from "view/components/DotsMenu";
import UploadIcon from "view/images/UploadIcon";
import EditIcon from "@material-ui/icons/Edit";
import {attachmentFileFormats} from "constraints/general";
import {UnpackedAttachmentFile} from "ts/api/attachmentFile";
import {convertFileUrl} from "tools/convertFileUrl";
import downloadBlobFile from "tools/file/downloadBlobFile";
import isPreviewableFile from "tools/file/isPreviewableFile";
import {useCopyTaskFileLink} from "hooks/file";
import CopyFileLinkButton from "view/components/CopyFileLinkButton";
import ModalSectionHeader from "../Drawer/ModalSectionHeader";
import useStyles from "./styles";
import FlexBox from "../Layout/FlexBox";
import AttachmentItem from "../AttachmentItem";

export type TaskAttachmentsProps = {
  title?: string;
  inputId?: string;
  hideTitle?: boolean;
  readonly?: boolean;
  /** Overrides the header's right-side slot (e.g. a library lock icon). */
  headerAction?: ReactNode;
  accept?: string;
  attachments: TaskAttachment[];
  className?: string;
  onClickEditSubTask?: () => void;
  onClikHistoryButton?: () => void;
  onEditAttachment?: (id: string) => void;
  onRemoveAttachment?: (id: string) => void;
  onAddAttachment?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCopyFileLinkAttachment?: (url: string) => void;
  renderAttachment?: (attachment: TaskAttachment, idx: number) => ReactNode;
  /** Read-only files listed without actions: removed since (FS-1572). */
  goneFileIds?: ReadonlySet<string>;
};

// TODO: Make this component more general and move to Drawer folder
const TaskAttachments: React.FC<TaskAttachmentsProps> = ({
  title,
  inputId,
  hideTitle,
  readonly = false,
  headerAction,
  accept,
  className,
  attachments,
  onAddAttachment,
  onEditAttachment,
  onRemoveAttachment,
  onClikHistoryButton,
  onClickEditSubTask,
  onCopyFileLinkAttachment,
  renderAttachment: renderAttachmentProps,
  goneFileIds,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const {t} = useTranslate();
  const resolvedTitle = title ?? t("common.tasks.attachments_title");
  const {copyFileLink, canShare} = useCopyTaskFileLink();

  const renderAttachment = (attachment: TaskAttachment, idx: number) => {
    const isGone =
      readonly && !!attachment.fileId && !!goneFileIds?.has(attachment.fileId);

    const renderActionPanel = () => {
      if (readonly) {
        if (isGone) {
          return null;
        }
        return (
          <FlexBox alignCenter>
            {/* Self-gates on `TaskFileShareProvider`: publishes first in the
                console, copies as-is on the email-review page. */}
            <CopyFileLinkButton
              fileId={attachment.fileId}
              url={attachment.url}
              className={classes.copyLinkButton}
            />
            <IconButton
              style={{padding: 8}}
              onClick={(e) => {
                e.stopPropagation();
                // Auth-gated files need a `?token=`. Open a blank tab
                // synchronously (preserves the user-gesture), then redirect
                // once `convertFileUrl` appends the token — a bare href 401s
                // on non-public files.
                const popup = window.open("", "_blank");
                convertFileUrl(attachment.url, attachment.provider)
                  .then(async (finalUrl) => {
                    // Previewable (pdf / image): render in the opened tab.
                    if (isPreviewableFile(attachment.extension)) {
                      if (popup) popup.location.href = finalUrl;
                      return;
                    }
                    // Downloadable type (xlsx, docx, ...) would leave a blank
                    // tab — close it and trigger a real download instead.
                    popup?.close();
                    const blob = await (await fetch(finalUrl)).blob();
                    await downloadBlobFile(
                      blob,
                      `${attachment.title}.${attachment.extension}`
                    );
                  })
                  .catch(() => {
                    if (popup) popup.location.href = attachment.url;
                  });
              }}
            >
              <NewTabIcon htmlColor={grey[500]} />
            </IconButton>
          </FlexBox>
        );
      }
      const isExhibitorUploadsResponse =
        onRemoveAttachment && !onEditAttachment;
      if (isExhibitorUploadsResponse) {
        return (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              return onRemoveAttachment && onRemoveAttachment(attachment.id);
            }}
          >
            <DeleteIcon htmlColor={grey[500]} />
          </IconButton>
        );
      }
      const isFileInStorage = !!attachment.url;
      const additionalMenuItems = isFileInStorage
        ? [
            {
              node: <Typography>Download</Typography>,
              divide: true,
              onClick: async (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                try {
                  // Token-augmented URL so the fetch passes the auth gate.
                  const fetchUrl = await convertFileUrl(
                    attachment.url,
                    attachment.provider
                  );
                  const link = document.createElement("a");
                  const fetchedBlob = await (await fetch(fetchUrl)).blob();
                  link.href = URL.createObjectURL(fetchedBlob);
                  link.download = `${attachment.title}.${attachment.extension}`;
                  link.click();
                } catch (e) {
                  throw new Error(`Unexpected error downloading file: ${e}`);
                }
              },
            },
            {
              node: <Typography>Copy file link</Typography>,
              divide: true,
              onClick: async (e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation();
                // Publish first, else the copied link 401s for everyone
                // outside the show. Falls back to the caller's plain copy
                // only where sharing isn't wired (no provider / no file id).
                if (canShare && attachment.fileId) {
                  await copyFileLink({
                    fileId: attachment.fileId,
                    url: attachment.url,
                  });
                  return;
                }
                if (onCopyFileLinkAttachment) {
                  onCopyFileLinkAttachment(attachment.url);
                }
              },
            },
          ]
        : [];

      return (
        <DotsMenu
          id={`attachment-action-menu-${idx}`}
          onClick={(e) => e.stopPropagation()}
          onClose={(e) => e.stopPropagation()}
          options={[
            {
              node: <Typography>Edit</Typography>,
              divide: !isFileInStorage,
              onClick: (e) => {
                e.stopPropagation();
                if (onEditAttachment) {
                  onEditAttachment(attachment.id);
                }
              },
            },
            ...additionalMenuItems,
            {
              node: <Typography color="error">Remove</Typography>,
              onClick: (e) => {
                e.stopPropagation();
                if (onRemoveAttachment) {
                  onRemoveAttachment(attachment.id);
                }
              },
            },
          ]}
        />
      );
    };

    return (
      <AttachmentItem
        attachment={attachment as UnpackedAttachmentFile}
        renderActionPanel={renderActionPanel}
        icon={<FileIcon extension={attachment.extension} />}
        isOpenable={!isGone}
      />
    );
  };

  const renderActionPanel = () => {
    if (!onAddAttachment) {
      return (
        <Box>
          {onClikHistoryButton && (
            <IconButton onClick={onClikHistoryButton}>
              <HistoryIcon htmlColor={grey[500]} />
            </IconButton>
          )}
          {onClickEditSubTask && (
            <IconButton onClick={onClickEditSubTask}>
              <EditIcon />
            </IconButton>
          )}
        </Box>
      );
    }
    const htmlInputId = `browse-attachment-${inputId}`;

    return (
      <Box>
        <input
          accept={accept || attachmentFileFormats}
          className={classes.fileInput}
          id={htmlInputId}
          type="file"
          multiple={false}
          onChange={onAddAttachment}
        />
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor={htmlInputId}>
          <Button
            color="primary"
            component="span"
            startIcon={<UploadIcon color={theme.palette.primary.main} />}
          >
            {t("common.action.upload")}
          </Button>
        </label>
      </Box>
    );
  };

  return (
    <Box marginBottom={3} className={className}>
      {!hideTitle && (
        <ModalSectionHeader
          title={resolvedTitle}
          actionPanel={headerAction ?? renderActionPanel()}
        />
      )}
      <FlexBox column gap={1.5}>
        {attachments.map(renderAttachmentProps ?? renderAttachment)}
      </FlexBox>
    </Box>
  );
};

export default TaskAttachments;
